/* eslint-disable max-statements */
import { Grid, LoadingOverlay, Switch } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useEffect } from 'react';

import { DeclarationHighValueGroupType, EnvironmentCountries } from '@e-origin/shared';

import { EucdmCode, MultiLangCodes, MultiSelect, Select, TextInput } from '@components';
import { ALLOWED_HV_REQUESTED_PROCEDURES } from '@constants';
import { useDeclarant } from '@hooks/declarant';
import { retrieveCodes } from '@hooks/retrieveCodes';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { useSettings } from '@hooks/useSettings';
import { BeforeSendingFormData } from './beforeSendingBatchFormIntializer';

interface FinalizeProcedureProps {
  form: UseFormReturnType<BeforeSendingFormData>;
  disabled: boolean;
  isExport: boolean;
}

export const FinalizeProcedure: React.FC<FinalizeProcedureProps> = (props) => {
  const { form, isExport } = props;
  const { data: declarant, isLoading } = useDeclarant();
  const { data: settings } = useSettings();

  const codelistSystems = useCodeListSystem({
    group: form.values.generalInfo?.group,
    isExport: isExport,
  });

  const ProcedureHeaderSwitch = () => (
    <Switch {...form.getInputProps('goodShipmentGlobal.procedureHeader')} checked={form.values.goodShipmentGlobal.procedureHeader} size="xs" />
  );

  if (!declarant) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  const selectedMessageType = form.values.generalInfo.group;
  const allowedProcedures = ALLOWED_HV_REQUESTED_PROCEDURES[selectedMessageType] ?? [];

  useEffect(() => {
    if (form.values.generalInfo.group === DeclarationHighValueGroupType.H5 && !form.values.declarationGoods.additionalProcedure?.includes('F15')) {
      form.setFieldValue('declarationGoods.additionalProcedure', [...form.values.declarationGoods.additionalProcedure, 'F15']);
    }
  }, [form.values.generalInfo.group]);

  return (
    <>
      <Grid mb="sm">
        {settings?.country === EnvironmentCountries.BE && (
          <Grid.Col span={4}>
            <Select
              {...form.getInputProps('generalInfo.group')}
              label="Message type"
              placeholder="Select a message type"
              data={Object.values(DeclarationHighValueGroupType).map((value) => ({ value, label: value }))}
              disabled={props.disabled || !form.values.goodShipmentGlobal.procedureHeader}
              searchable
              data-pw="message-type-input"
            />
          </Grid.Col>
        )}
        <Grid.Col span={4}>
          <EucdmCode path="goodShipmentGlobal.customsProcedure.current">
            <MultiLangCodes
              {...form.getInputProps('customsProcedureCurrent')}
              label="Requested procedure"
              code="CL092"
              includeList={allowedProcedures.length > 0 ? allowedProcedures : undefined}
              system={codelistSystems}
              onChange={(selectedOption) => form.setFieldValue('customsProcedureCurrent', selectedOption)}
              rightSection={<ProcedureHeaderSwitch />}
              disabled={props.disabled || !form.values.goodShipmentGlobal.procedureHeader}
              data-pw="requested-procedure-input"
            />
          </EucdmCode>
        </Grid.Col>
        <Grid.Col span={4}>
          <EucdmCode path="goodShipmentGlobal.customsProcedure.previous">
            <MultiLangCodes
              {...form.getInputProps('customsProcedurePrevious')}
              label="Previous procedure"
              code="CL093"
              system={codelistSystems}
              onChange={(selectedOption) => form.setFieldValue('customsProcedurePrevious', selectedOption)}
              rightSection={<ProcedureHeaderSwitch />}
              disabled={props.disabled || !form.values.goodShipmentGlobal.procedureHeader}
              data-pw="previous-procedure-input"
            />
          </EucdmCode>
        </Grid.Col>
      </Grid>

      <Grid mb="sm">
        {!isExport && (
          <Grid.Col span={4}>
            <EucdmCode path="goodShipmentGlobal.preference">
              <MultiLangCodes
                {...form.getInputProps('goodShipmentGlobal.preference')}
                label="Preference"
                code="CL603"
                system={codelistSystems}
                onChange={(selectedOption) => form.setFieldValue('goodShipmentGlobal.preference', selectedOption)}
                rightSection={<ProcedureHeaderSwitch />}
                disabled={props.disabled || !form.values.goodShipmentGlobal.procedureHeader}
                data-pw="preference-input"
              />
            </EucdmCode>
          </Grid.Col>
        )}
        <Grid.Col span={4}>
          <EucdmCode path="goodsShipmentItems.additionalProcedure">
            <MultiSelect
              {...form.getInputProps('declarationGoods.additionalProcedure')}
              searchable
              clearable
              hidePickedOptions
              label="Additional procedure"
              data-pw="additional-procedure-input"
              fetchOptions={async (search: string) => await retrieveCodes('AdditionalProcedure', search, declarant.language)}
              disabled={props.disabled || !form.values.goodShipmentGlobal.procedureHeader}
            />
          </EucdmCode>
        </Grid.Col>
      </Grid>

      {settings?.country === EnvironmentCountries.BE && (
        <Grid mb="sm">
          <Grid.Col span={4}>
            <EucdmCode path="goodShipmentGlobal.customsProcedure.type">
              <TextInput
                {...form.getInputProps('goodShipmentGlobal.customsProcedureType')}
                label="Procedure Type"
                rightSection={<ProcedureHeaderSwitch />}
                disabled={props.disabled || !form.values.goodShipmentGlobal.procedureHeader}
                data-pw="procedure-type-input"
              />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={4}>
            <TextInput
              {...form.getInputProps('goodShipmentGlobal.customsNationalProcedure')}
              label="National procedure"
              rightSection={<ProcedureHeaderSwitch />}
              disabled={props.disabled || !form.values.goodShipmentGlobal.procedureHeader}
              data-pw="national-procedure-input"
            />
          </Grid.Col>
          <Grid.Col span={4}>
            <MultiLangCodes
              {...form.getInputProps('goodShipmentGlobal.customsNationalProcedureCode')}
              label="National procedure code"
              code="CL457"
              system={codelistSystems}
              onChange={(selectedOption) => form.setFieldValue('goodShipmentGlobal.customsNationalProcedureCode', selectedOption)}
              rightSection={<ProcedureHeaderSwitch />}
              disabled={props.disabled || !form.values.goodShipmentGlobal.procedureHeader}
              data-pw="national-procedure-code-input"
            />
          </Grid.Col>
        </Grid>
      )}
    </>
  );
};
